import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useInventorySummary, useMovementSummary } from '../hooks/useWmsInventory';
import { useLocationSummary } from '../hooks/useWmsLocations';
import { useWorkOrderSummary } from '../hooks/useWmsWorkOrders';
import { usePickingSummary } from '../hooks/useWmsPicking';
import { useShippingSummary } from '../hooks/useWmsShipping';
import { useReceivingSummary } from '../hooks/useWmsReceiving';
import { InventorySummaryCard } from '../components/dashboard/InventorySummaryCard';
import { LowStockAlerts } from '../components/dashboard/LowStockAlerts';
import { ZoneSummary } from '../components/dashboard/ZoneSummary';
import { RecentMovements } from '../components/dashboard/RecentMovements';
import { WorkOrderSummaryCard } from '../components/dashboard/WorkOrderSummaryCard';
import { PickingSummaryCard } from '../components/dashboard/PickingSummaryCard';
import { ShippingSummaryCard } from '../components/dashboard/ShippingSummaryCard';
import { ReceivingSummaryCard } from '../components/dashboard/ReceivingSummaryCard';
import type { InventorySummary, MovementSummary, LocationSummary, WorkOrderSummary, PickingSummary, ShippingSummary, ReceivingSummary } from '../types';

export default function WmsDashboardPage() {
  const inventoryQuery = useInventorySummary();
  const movementQuery = useMovementSummary(7);
  const locationQuery = useLocationSummary();
  const workOrderQuery = useWorkOrderSummary();
  const pickingQuery = usePickingSummary();
  const shippingQuery = useShippingSummary();
  const receivingQuery = useReceivingSummary();

  const inventorySummary = inventoryQuery.data as InventorySummary | undefined;
  const movementSummary = movementQuery.data as MovementSummary | undefined;
  const locationSummary = locationQuery.data as LocationSummary | undefined;
  const workOrderSummary = workOrderQuery.data as WorkOrderSummary | undefined;
  const pickingSummary = pickingQuery.data as PickingSummary | undefined;
  const shippingSummary = shippingQuery.data as ShippingSummary | undefined;
  const receivingSummary = receivingQuery.data as ReceivingSummary | undefined;
  const inventoryLoading = inventoryQuery.isLoading;
  const movementsLoading = movementQuery.isLoading;
  const locationsLoading = locationQuery.isLoading;
  const workOrdersLoading = workOrderQuery.isLoading;
  const pickingLoading = pickingQuery.isLoading;
  const shippingLoading = shippingQuery.isLoading;
  const receivingLoading = receivingQuery.isLoading;

  const hasLocations = (locationSummary?.totals?.total_zones || 0) > 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
          Workshop <small>Inventory management dashboard</small>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/wms/locations">Locations</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/wms/work-orders">Work Orders</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/wms/picking">Picking</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/wms/shipping">Shipping</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/procurement">Receiving</Link>
          </Button>
          <Button asChild>
            <Link to="/wms/inventory">View Inventory</Link>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <InventorySummaryCard summary={inventorySummary} isLoading={inventoryLoading} />

      {/* Setup prompt if no locations */}
      {!locationsLoading && !hasLocations && (
        <div className="p-8 border-2 border-dashed rounded-lg text-center">
          <h2 className="text-lg font-semibold mb-2">Get Started with WMS</h2>
          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
            Set up your workshop zones and bin locations to start tracking inventory.
            Begin by creating zones like Expedition, Inventory, and Calibration.
          </p>
          <Button asChild>
            <Link to="/wms/locations">Setup Locations</Link>
          </Button>
        </div>
      )}

      {/* Main Content Grid */}
      {hasLocations && (
        <>
          {/* Workflow Summary Row */}
          <div className="grid gap-6 lg:grid-cols-2">
            <ReceivingSummaryCard
              summary={receivingSummary}
              isLoading={receivingLoading}
            />
            <WorkOrderSummaryCard
              summary={workOrderSummary}
              isLoading={workOrdersLoading}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <PickingSummaryCard
              summary={pickingSummary}
              isLoading={pickingLoading}
            />
            <ShippingSummaryCard
              summary={shippingSummary}
              isLoading={shippingLoading}
            />
          </div>

          {/* Inventory Details Grid */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-6">
              <LowStockAlerts
                items={inventorySummary?.low_stock_items || []}
                isLoading={inventoryLoading}
              />
              <ZoneSummary
                byZone={inventorySummary?.by_zone || []}
                isLoading={inventoryLoading}
              />
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <RecentMovements
                movements={movementSummary?.recent_movements || []}
                isLoading={movementsLoading}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
